/*  JS of Predictive Search Custom Code
    Restored with custom B2B features and fixed cart functionality
    --------------------------------------
    This version keeps all custom B2B features while using Empire theme cart patterns
*/

(() => {
  "use strict";

  // --- UTILITY ---
  const debounce = (fn, delay) => {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  // --- CORE LOGIC ---

  /**
   * The single source of truth for updating the cart from the predictive search.
   * This function is called for ATC clicks, +/- clicks.
   * Uses Empire theme's AddToCartFlyout pattern but with custom B2B features.
   * @param {HTMLElement} productCard The .search-flydown--product-card element.
   */
  const updateCartFromPredictiveSearch = async productCard => {
    if (!productCard) return;

    const variantId = productCard.dataset.variantId;
    const quantityInput = productCard.querySelector(".quantity-selector__input");
    if (!variantId || !quantityInput) return;

    const inputQuantity = parseInt(quantityInput.value, 10) || 1;

    const atcButton = productCard.querySelector(".add-to-cart-btn");

    // Disable all buttons and show processing state
    productCard.querySelectorAll("button").forEach(b => (b.disabled = true));
    if (atcButton) atcButton.classList.add("processing");

    try {
      // Check if product is already in cart
      const cartItemKey = productCard.getAttribute("data-cartitem-key");
      const existingQuantity = parseInt(productCard.getAttribute("data-cartitem-quantity") || "0", 10);

      let response;
      if (cartItemKey && existingQuantity > 0) {
        // Product is already in cart, use update endpoint with input quantity as new total
        const formData = new FormData();
        formData.append("updates[" + cartItemKey + "]", inputQuantity);

        response = await fetch("/cart/update.js", {
          method: "POST",
          body: formData,
        });
      } else {
        // Product not in cart, use add endpoint
        const formData = new FormData();
        formData.append("id", variantId);
        formData.append("quantity", inputQuantity);

        response = await fetch("/cart/add.js", {
          method: "POST",
          body: formData,
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} from cart API`);
      }

      const data = await response.json();

      // Get updated cart data
      const cartData = Shopify.cart;

      // Update header cart badge using Empire theme pattern
      const countEvent = new CustomEvent("cartcount:update", {
        detail: cartData,
      });
      window.dispatchEvent(countEvent);

      // Flash success on the ATC button
      if (atcButton) {
        atcButton.classList.add("added-success");
        setTimeout(() => atcButton.classList.remove("added-success"), 1200);

        // Show the success toast if available
        if (typeof window.showSuccessToast === "function") {
          window.showSuccessToast();
        }
      }

      // Refresh the drawer if available
      if (window.Shopify?.theme?.cartDrawer) {
        await window.Shopify.theme.cartDrawer.refresh();
        window.Shopify.theme.cartDrawer.open();
      } else {
        document.dispatchEvent(new CustomEvent("cart-drawer:refresh"));
        document.dispatchEvent(new CustomEvent("cart-drawer:open"));
      }

      // Update pricing highlights in predictive search dropdown using the fresh cart data
      updatePredictiveSearchPricingWithCart(cartData);

      // If on the cart page, refresh the main section as well
      if (document.body.classList.contains("template-cart") && typeof window.refreshCartPageSection === "function") {
        await window.refreshCartPageSection();
        document.dispatchEvent(new CustomEvent("promos:refresh"));
      }
    } catch (error) {
      console.error("[Predictive Search] Cart update error:", error);
    } finally {
      // Re-enable all buttons and remove processing state
      productCard.querySelectorAll("button").forEach(b => (b.disabled = false));
      if (atcButton) atcButton.classList.remove("processing");

      // Update button states after cart update
      refreshPredictiveSearchCardState(productCard);
    }
  };

  /**
   * Enable / disable ATC button and plus button according to the quantity and
   * the effective limit for the product in predictive search.
   */
  function refreshPredictiveSearchCardState(productCard) {
    const atc = productCard.querySelector(".add-to-cart-btn");
    const qtyI = productCard.querySelector(".quantity-selector__input");
    const plus = productCard.querySelector(".quantity-selector__button--plus");
    const minusWrapper = productCard.querySelector(".quantity-selector__button-wrapper--minus");
    if (!atc || !qtyI) return;

    const qty = parseInt(qtyI.value, 10) || 1;
    const limit = window.getEffectiveLimit ? window.getEffectiveLimit(atc) : 0;

    /* ATC state ------------------------------------------------------------- */
    if (limit > 0 && qty >= limit + 1) {
      atc.classList.add("disabled", "limit-disabled");
      atc.setAttribute("disabled", "disabled");
    } else {
      atc.classList.remove("disabled", "limit-disabled");
      atc.removeAttribute("disabled");
    }

    /* + button state -------------------------------------------------------- */
    if (plus) {
      if (limit > 0 && qty >= limit + 1) plus.setAttribute("disabled", "disabled");
      else plus.removeAttribute("disabled");
    }

    /* - button state -------------------------------------------------------- */
    if (minusWrapper) {
      if (qty <= 1) {
        minusWrapper.classList.add("quantity-selector__button-wrapper--disabled");
      } else {
        minusWrapper.classList.remove("quantity-selector__button-wrapper--disabled");
      }
    }
  }

  /**
   * Update volume pricing highlights in predictive search dropdown using cart data.
   * Uses total cart quantity PLUS the quantity to add for each product to determine
   * which price tier should be highlighted.
   */
  function updatePredictiveSearchPricingWithCart(cartData) {
    const resultsContainer = document.querySelector("[data-live-search-results]");
    if (!resultsContainer) return;

    // Calculate base cart quantity from the provided cart data
    let baseCartQty = 0;
    if (cartData && cartData.items) {
      baseCartQty = cartData.items.reduce((sum, item) => sum + item.quantity, 0);
    } else {
      return;
    }

    // Update each product card individually, taking into account the quantity to add
    resultsContainer.querySelectorAll(".search-flydown--product-card").forEach(card => {
      const grid = card.querySelector(".b2b-pricing-grid");
      const quantityInput = card.querySelector(".quantity-selector__input");

      if (grid && quantityInput && typeof window.highlightCurrentTier === "function") {
        const qtyToAdd = parseInt(quantityInput.value, 10) || 1;

        // Get existing quantity for this specific product from cart
        const existingQty = parseInt(card.getAttribute("data-cartitem-quantity") || "0", 10);

        // Calculate total: base cart qty + quantity we're planning to add for this product
        // But subtract existing quantity of this product to avoid double counting
        const totalQty = baseCartQty - existingQty + qtyToAdd;

        window.highlightCurrentTier(grid, totalQty);
      }
    });
  }

  /**
   * Update volume pricing highlights in predictive search dropdown using cart data.
   * Uses total cart quantity PLUS the quantity to add for each product to determine
   * which price tier should be highlighted.
   */
  function updatePredictiveSearchPricing() {
    const resultsContainer = document.querySelector("[data-live-search-results]");
    if (!resultsContainer) return;

    // Calculate base cart quantity using cart API (more reliable)
    let baseCartQty = 0;
    try {
      const cartData = Shopify.cart;
      baseCartQty = cartData.items.reduce((total, item) => total + item.quantity, 0);
    } catch (error) {
      // Fallback: use existing method
      if (typeof window.getTotalCartItemCount === "function") {
        baseCartQty = window.getTotalCartItemCount();
      }
    }

    // Update each product card individually, taking into account the quantity to add
    resultsContainer.querySelectorAll(".search-flydown--product-card").forEach(card => {
      const grid = card.querySelector(".b2b-pricing-grid");
      const quantityInput = card.querySelector(".quantity-selector__input");

      if (grid && quantityInput && typeof window.highlightCurrentTier === "function") {
        const qtyToAdd = parseInt(quantityInput.value, 10) || 1;

        // Get existing quantity for this specific product from cart
        const existingQty = parseInt(card.getAttribute("data-cartitem-quantity") || "0", 10);

        // Calculate total: base cart qty + quantity we're planning to add for this product
        // But subtract existing quantity of this product to avoid double counting
        const totalQty = baseCartQty - existingQty + qtyToAdd;

        window.highlightCurrentTier(grid, totalQty);
      }
    });
  }

  /**
   * Main handler for all interactions within the predictive search.
   */
  const predictiveSearchClickHandler = async e => {
    const target = e.target;

    // Handle Add to Cart button clicks
    if (target.matches(".add-to-cart-btn") || target.closest(".add-to-cart-btn")) {
      e.preventDefault();
      e.stopPropagation();

      const productCard = target.closest("[data-variant-id]");
      if (productCard) {
        updateCartFromPredictiveSearch(productCard);
        return;
      }
    }

    // Handle +/- Clicks
    if (target.matches(".quantity-selector__button--plus") || target.matches(".quantity-selector__button--minus")) {
      e.preventDefault();
      const productCard = target.closest("[data-variant-id]");
      if (!productCard) return;

      const quantityInput = productCard.querySelector(".quantity-selector__input");
      if (!quantityInput) return;

      const amount = target.matches(".quantity-selector__button--plus") ? 1 : -1;
      const newValue = Math.max(1, parseInt(quantityInput.value, 10) + amount);
      quantityInput.value = newValue;

      // Update button states after quantity change
      refreshPredictiveSearchCardState(productCard);

      // Update pricing highlights when quantity changes - this will now take into account the new quantity
      await updatePredictiveSearchPricing();
    }
  };

  /**
   * Handler for quantity input changes (when user types directly)
   */
  const predictiveSearchInputHandler = async e => {
    const target = e.target;

    // Handle quantity input changes
    if (target.matches(".quantity-selector__input")) {
      const productCard = target.closest("[data-variant-id]");
      if (!productCard) return;
      // Ensure minimum value
      // const newValue = Math.max(1, parseInt(target.value, 10) || 1);
      // if (target.value !== newValue.toString()) {
      //   target.value = newValue;
      // }

      // Update button states after quantity change
      refreshPredictiveSearchCardState(productCard);

      // Update pricing highlights when quantity changes
      await updatePredictiveSearchPricing();
    }
  };

  const predictiveSearchSubmitHandler = e => {
    // This handler is attached to the results container, so any form submission inside will bubble up.
    // We must prevent the default behavior to stop redirection and handle it with AJAX.
    e.preventDefault();
    e.stopPropagation();

    const form = e.target;

    // Only handle predictive search forms
    if (!form.classList.contains("predictive-search-form")) {
      return;
    }

    const productCard = form.closest("[data-variant-id]");

    // Ensure we found a product card associated with the submitted form.
    if (productCard) {
      updateCartFromPredictiveSearch(productCard);
    }
  };

  // --- INITIALIZATION ---
  const initializePredictiveSearch = () => {
    const attachListeners = () => {
      const resultsContainer = document.querySelector("[data-live-search-results]");
      if (resultsContainer && !resultsContainer.dataset.finalCartListeners) {
        resultsContainer.dataset.finalCartListeners = "true";
        resultsContainer.addEventListener("click", predictiveSearchClickHandler);
        resultsContainer.addEventListener("submit", predictiveSearchSubmitHandler);
        resultsContainer.addEventListener("input", predictiveSearchInputHandler);

        // Initialize button states for existing cards
        resultsContainer.querySelectorAll("[data-variant-id]").forEach(card => {
          refreshPredictiveSearchCardState(card);
        });

        // Also refresh button states after a short delay to ensure cart data is loaded
        setTimeout(() => {
          resultsContainer.querySelectorAll("[data-variant-id]").forEach(card => {
            refreshPredictiveSearchCardState(card);
          });
        }, 100);

        // Initialize pricing highlights
        (async () => await updatePredictiveSearchPricing())();

        // Listen for cart updates to refresh button states
        window.addEventListener("cartcount:update", () => {
          resultsContainer.querySelectorAll("[data-variant-id]").forEach(card => {
            refreshPredictiveSearchCardState(card);
          });
        });

        return true;
      }
      return false;
    };

    // Try to attach immediately
    if (attachListeners()) {
      return;
    }

    // If not found, use observer
    const observer = new MutationObserver(() => {
      if (attachListeners()) {
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  // Initialize immediately and also when custom cart is ready
  initializePredictiveSearch();

  // Simple function to adjust flydown height for single product with promotions
  function adjustFlydownHeight() {
    const flydown = document.querySelector("[data-live-search-flydown]");
    if (!flydown) return;

    const productCards = flydown.querySelectorAll(".search-flydown--product-card");
    const hasPromotions = flydown.querySelector(".predictive-promo-preview");

    if (productCards.length === 1 && hasPromotions) {
      flydown.style.setProperty("--open-height", "400px");
    }
  }

  // Check every 500ms
  setInterval(adjustFlydownHeight, 500);
})();
